import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { PropTypes } from 'prop-types';

import { GuidService } from 'ui-components/services/guid-service';

import { useWorkflowData } from '../../fulfillment-planner/hooks/use-workflow-guids-state';
import { useDeprecatedLocalStorage, WORKFLOW_DATA } from '../../hooks/use-local-storage';

export const SegmentEventWorkflowTrackingContext = createContext({});

export const useSegmentEventWorkflowTrackingContext = () => useContext(SegmentEventWorkflowTrackingContext);

export const SegmentEventWorkflowTrackingContextProvider = React.memo(({ children }) => {
  const [workflowDataObjectInLocalStorage, setWorkflowDataObjectInLocalStorage] = useDeprecatedLocalStorage(WORKFLOW_DATA, {});
  const workflowData = useRef(workflowDataObjectInLocalStorage);
  const { setWorkflowDataObject } = useWorkflowData(workflowData.current);

  const updateWorkflowData = useCallback(
    (workflowId, metadata = {}, shouldReset) => {
      const existingContext = shouldReset ? { workflowGuid: GuidService.newGuid() } : workflowData.current[workflowId];
      const newContext = { ...existingContext, ...metadata };
      workflowData.current = {
        ...workflowData.current,
        [workflowId]: newContext,
      };
      setWorkflowDataObject(workflowData.current);
      setWorkflowDataObjectInLocalStorage(workflowData.current);
      return newContext;
    },
    [setWorkflowDataObject, setWorkflowDataObjectInLocalStorage],
  );

  const getWorkflowData = useCallback(
    (workflowId) => {
      if (!workflowData.current[workflowId]) {
        return updateWorkflowData(workflowId);
      }
      return workflowData.current[workflowId];
    },
    [updateWorkflowData],
  );

  const value = useMemo(
    () => ({
      updateWorkflowData,
      getWorkflowData,
    }),
    [updateWorkflowData, getWorkflowData],
  );

  return <SegmentEventWorkflowTrackingContext.Provider value={value}>{children}</SegmentEventWorkflowTrackingContext.Provider>;
});

SegmentEventWorkflowTrackingContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
